/**
 * SupplierAutocomplete — elige un proveedor buscándolo EN EL SERVIDOR.
 *
 * Reemplaza el `<select>` nativo que mostraba los primeros 100 proveedores y no
 * buscaba: en un tenant con más proveedores, los que quedaban fuera no se podían
 * elegir al registrar una factura o un pago (H-M03-01, plan de pruebas M03). Es
 * el mismo defecto que tenía el catálogo de productos (#1396).
 *
 * Busca por razón social, nombre de fantasía, RUT (con o sin puntos) o correo.
 */
import { useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import { Autocomplete, type AutocompleteItem } from '@/components/Autocomplete'
import { useDebounced } from '@/hooks/useDebounced'
import { formatRut } from '@/lib/utils'
import { purchaseKeys, type SupplierListResponse } from '../hooks/usePurchases'

const RESULTADOS = 20

interface Props {
  /** El id del proveedor elegido ('' si no hay). */
  value: string
  onChange: (supplierId: string) => void
  /** Lo que se muestra cuando ya viene un proveedor elegido (al editar). */
  initialLabel?: string
  id?: string
  'aria-label'?: string
  className?: string
  placeholder?: string
}

export function SupplierAutocomplete({
  value, onChange, initialLabel = '', id, 'aria-label': ariaLabel, className,
  placeholder = 'Buscar por razón social o RUT...',
}: Props) {
  const [texto, setTexto] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [abierto, setAbierto] = useState(false)
  const termino = useDebounced(query.trim(), 250)

  const params = { active: 'active', search: termino || undefined, page: 1, page_size: RESULTADOS }
  const { data, isFetching } = useQuery<SupplierListResponse>({
    queryKey: purchaseKeys.suppliers(params),
    queryFn: async () => (await apiClient.get('/purchases/suppliers/', { params })).data,
    enabled: abierto,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })
  const resultados = useMemo(() => data?.suppliers ?? [], [data])
  const items: AutocompleteItem[] = useMemo(() => resultados.map(s => ({
    id: s.id, label: s.legal_name, sublabel: s.tax_id ? formatRut(s.tax_id) : undefined,
  })), [resultados])

  // Sin texto propio se muestra el rótulo de lo ya elegido.
  const mostrado = texto ?? (value ? initialLabel : '')

  return (
    <Autocomplete
      items={items}
      value={mostrado}
      onChange={t => {
        // Escribir de nuevo invalida la selección anterior.
        setTexto(t)
        if (value) onChange('')
      }}
      onQueryChange={q => { setQuery(q); setAbierto(true) }}
      onSelect={item => {
        setTexto(item.sublabel ? `${item.label} — ${item.sublabel}` : item.label)
        onChange(item.id)
      }}
      serverFiltered
      loading={isFetching || query.trim() !== termino}
      placeholder={placeholder}
      className={className}
      id={id}
      aria-label={ariaLabel}
      freeText
    />
  )
}
